import React, { useMemo, useState } from 'react';
import { normaliseSegments } from './transcriptSegments';

/**
 * Read-only transcript for a finished scribe session.
 *
 * LiveTranscript renders the stream while a visit is being recorded. This is the
 * after-the-fact view, and it reads a file out of S3 rather than a socket, so the
 * shape is whatever was stored — which is not one thing:
 *
 *   - HealthScribe batch output (session.hs_transcript_key, the usual case):
 *     { Conversation: { TranscriptSegments: [ { Content, BeginAudioTime,
 *       ParticipantDetails: { ParticipantRole: 'CLINICIAN_0' } } ] } }
 *   - the streaming shape we build in AmbientScribeService, if a practice-scoped
 *     copy was written: { segments: [ { text, role, startTime } ] }
 *
 * Roles arrive as CLINICIAN_0 / PATIENT_0 in batch output and lowercase in the
 * streaming shape. normaliseSegments flattens both to one form; anything it does
 * not recognise falls through to the raw JSON view rather than rendering blank,
 * because a transcript that silently shows nothing is worse than an ugly one.
 */
function SessionTranscript({ transcript, loading, error, onRetry }) {
  const [showRaw, setShowRaw] = useState(false);

  const segments = useMemo(() => normaliseSegments(transcript), [transcript]);

  if (loading) {
    return <div style={styles.state}>Loading transcript…</div>;
  }

  if (error) {
    return (
      <div style={styles.state}>
        <p style={styles.errorText}>{error}</p>
        {onRetry && (
          <button type="button" style={styles.retryBtn} onClick={onRetry}>
            Try again
          </button>
        )}
      </div>
    );
  }

  // The endpoint answers 200 with transcript: null when neither S3 key resolved.
  if (!transcript) {
    return (
      <div style={styles.state}>
        <p style={styles.emptyText}>
          No transcript was stored for this session. Recordings made before transcription
          finished, or sessions that ended early, will not have one.
        </p>
      </div>
    );
  }

  if (segments === null) {
    return (
      <div style={styles.state}>
        <p style={styles.emptyText}>
          This transcript is stored in a format this view does not recognise.
        </p>
        <button type="button" style={styles.retryBtn} onClick={() => setShowRaw(v => !v)}>
          {showRaw ? 'Hide raw transcript' : 'Show raw transcript'}
        </button>
        {showRaw && <pre style={styles.raw}>{JSON.stringify(transcript, null, 2)}</pre>}
      </div>
    );
  }

  if (segments.length === 0) {
    return (
      <div style={styles.state}>
        <p style={styles.emptyText}>The transcript for this session is empty.</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.toolbar}>
        <span style={styles.count}>
          {segments.length} {segments.length === 1 ? 'segment' : 'segments'}
        </span>
      </div>
      <ol style={styles.list}>
        {segments.map((seg, idx) => (
          <li key={seg.id ?? idx} style={styles.entry}>
            <span style={{ ...styles.role, color: seg.role === 'clinician' ? '#2563eb' : '#059669' }}>
              {seg.role === 'clinician' ? 'Dr' : 'Patient'}:
            </span>
            <span style={styles.text}>{seg.text}</span>
            {seg.startTime !== null && seg.startTime !== undefined && (
              <span style={styles.timestamp}>{formatTimestamp(seg.startTime)}</span>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
}

function formatTimestamp(seconds) {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

const styles = {
  container: { display: 'flex', flexDirection: 'column', gap: '8px' },
  toolbar: { display: 'flex', justifyContent: 'flex-end' },
  count: { fontSize: '12px', color: '#6b7280' },
  list: { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '2px' },
  entry: {
    display: 'flex',
    gap: '8px',
    alignItems: 'baseline',
    padding: '6px 0',
    borderBottom: '1px solid #f3f4f6',
    lineHeight: '1.5',
  },
  role: { fontWeight: 600, fontSize: '13px', whiteSpace: 'nowrap', minWidth: '60px' },
  text: { fontSize: '14px', color: '#1f2937', flex: 1, whiteSpace: 'pre-wrap' },
  timestamp: { fontSize: '11px', color: '#9ca3af', whiteSpace: 'nowrap', fontFamily: 'monospace' },
  state: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
    padding: '40px 20px',
    color: '#6b7280',
  },
  emptyText: { margin: 0, fontSize: '14px', textAlign: 'center', maxWidth: '420px' },
  errorText: { margin: 0, fontSize: '14px', textAlign: 'center', color: '#b91c1c' },
  retryBtn: {
    padding: '6px 14px',
    fontSize: '13px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    background: '#fff',
    cursor: 'pointer',
  },
  raw: {
    width: '100%',
    maxHeight: '360px',
    overflow: 'auto',
    background: '#f9fafb',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '12px',
    fontSize: '12px',
    textAlign: 'left',
  },
};

export default SessionTranscript;
